/**
 * CTA Dialog Context
 *
 * Global context for managing dialogs triggered by CTA handlers
 * This allows CTA handlers to open dialogs programmatically
 */

'use client';

import React, {
  createContext,
  use,
  useState,
  useCallback,
  useRef
} from 'react';
import { CTADialogRenderer } from './cta-dialog-renderer';
import type { FileUploadType } from './cta-types';

// Dialog state interfaces
interface FileUploadDialogState {
  open: boolean;
  matterId: number;
  fileType: FileUploadType;
}

interface ContactCreateDialogState {
  open: boolean;
  matterId: number;
  isLegalPersonalRepresentative?: boolean;
  isBeneficiary?: boolean;
}

interface InventoryCreateDialogState {
  open: boolean;
  matterId: number;
  inventoryType?: string;
}

interface CorrespondenceDialogState {
  open: boolean;
  matterId: number;
  institutionId: number;
  type: 'notification' | 'follow-up' | 'collection-request';
  parentCorrespondenceId?: number; // This is now the draft correspondence ID for follow-ups
}

interface EstateDetailsDialogState {
  open: boolean;
  matterId: number;
}

interface ManageMatterDialogState {
  open: boolean;
  matterId: number;
}

interface NotificationsWorkflowDialogState {
  open: boolean;
  matterId: number;
}

interface CollectionWorkflowDialogState {
  open: boolean;
  matterId: number;
}

interface ViewFormsDialogState {
  open: boolean;
  matterId: number;
  institutionId: number;
  institutionName: string;
}

// Context value interface
interface CTADialogContextValue {
  // File upload dialog
  fileUploadDialog: FileUploadDialogState | null;
  openFileUploadDialog: (matterId: number, fileType: FileUploadType) => void;
  closeFileUploadDialog: () => void;

  // Contact creation dialog
  contactCreateDialog: ContactCreateDialogState | null;
  openContactCreateDialog: (
    matterId: number,
    options?: {
      isLegalPersonalRepresentative?: boolean;
      isBeneficiary?: boolean;
    }
  ) => void;
  closeContactCreateDialog: () => void;

  // Inventory creation dialog
  inventoryCreateDialog: InventoryCreateDialogState | null;
  openInventoryCreateDialog: (matterId: number, inventoryType?: string) => void;
  closeInventoryCreateDialog: () => void;

  // Correspondence dialog
  correspondenceDialog: CorrespondenceDialogState | null;
  openCorrespondenceDialog: (
    matterId: number,
    institutionId: number,
    type: 'notification' | 'follow-up' | 'collection-request',
    parentCorrespondenceId?: number
  ) => void;
  closeCorrespondenceDialog: () => void;

  // Estate details dialog
  estateDetailsDialog: EstateDetailsDialogState | null;
  openEstateDetailsDialog: (matterId: number) => void;
  closeEstateDetailsDialog: () => void;

  // Manage matter dialog (non-estate)
  manageMatterDialog: ManageMatterDialogState | null;
  openManageMatterDialog: (matterId: number) => void;
  closeManageMatterDialog: () => void;

  // Notifications workflow dialog
  notificationsWorkflowDialog: NotificationsWorkflowDialogState | null;
  openNotificationsWorkflow: (matterId: number) => void;
  closeNotificationsWorkflow: () => void;

  // Collection workflow dialog
  collectionWorkflowDialog: CollectionWorkflowDialogState | null;
  openCollectionWorkflow: (matterId: number) => void;
  closeCollectionWorkflow: () => void;

  // View Forms dialog
  viewFormsDialog: ViewFormsDialogState | null;
  openViewFormsDialog: (
    matterId: number,
    institutionId: number,
    institutionName: string
  ) => void;
  closeViewFormsDialog: () => void;

  // Discovery sheets (registered by DiscoverySheetProvider when mounted)
  openLetterEditorSheet: (institutionId: number) => void;
  openCorrespondenceSheet: (institutionId: number) => void;
  openFollowUpSheet: (institutionId: number) => void;
  registerDiscoverySheets: (
    handlers: {
      openLetterEditorSheet: (institutionId: number) => void;
      openCorrespondenceSheet: (institutionId: number) => void;
      openFollowUpSheet: (institutionId: number) => void;
    } | null
  ) => void;

  // Requirements sheet (registered by RedemptionSheetProvider when mounted)
  openRequirementsSheet: (institutionId: number) => void;
  registerRequirementsSheet: (
    handler: ((institutionId: number) => void) | null
  ) => void;

  // Redemption letter editor sheet (registered by RedemptionSheetProvider when mounted)
  openRedemptionLetterEditorSheet: (institutionId: number) => void;
  registerRedemptionLetterEditorSheet: (
    handler: ((institutionId: number) => void) | null
  ) => void;
}

// Create context
const CTADialogContext = createContext<CTADialogContextValue | undefined>(
  undefined
);

// Provider component
export function CTADialogProvider({ children }: { children: React.ReactNode }) {
  // Dialog states
  const [fileUploadDialog, setFileUploadDialog] =
    useState<FileUploadDialogState | null>(null);
  const [contactCreateDialog, setContactCreateDialog] =
    useState<ContactCreateDialogState | null>(null);
  const [inventoryCreateDialog, setInventoryCreateDialog] =
    useState<InventoryCreateDialogState | null>(null);
  const [correspondenceDialog, setCorrespondenceDialog] =
    useState<CorrespondenceDialogState | null>(null);
  const [estateDetailsDialog, setEstateDetailsDialog] =
    useState<EstateDetailsDialogState | null>(null);
  const [manageMatterDialog, setManageMatterDialog] =
    useState<ManageMatterDialogState | null>(null);
  const [notificationsWorkflowDialog, setNotificationsWorkflowDialog] =
    useState<NotificationsWorkflowDialogState | null>(null);
  const [collectionWorkflowDialog, setCollectionWorkflowDialog] =
    useState<CollectionWorkflowDialogState | null>(null);
  const [viewFormsDialog, setViewFormsDialog] =
    useState<ViewFormsDialogState | null>(null);

  // File upload dialog handlers
  const openFileUploadDialog = useCallback(
    (matterId: number, fileType: FileUploadType) => {
      setFileUploadDialog({ open: true, matterId, fileType });
    },
    []
  );

  const closeFileUploadDialog = useCallback(() => {
    setFileUploadDialog(null);
  }, []);

  // Contact creation dialog handlers
  const openContactCreateDialog = useCallback(
    (
      matterId: number,
      options?: {
        isLegalPersonalRepresentative?: boolean;
        isBeneficiary?: boolean;
      }
    ) => {
      setContactCreateDialog({
        open: true,
        matterId,
        ...options
      });
    },
    []
  );

  const closeContactCreateDialog = useCallback(() => {
    setContactCreateDialog(null);
  }, []);

  // Inventory creation dialog handlers
  const openInventoryCreateDialog = useCallback(
    (matterId: number, inventoryType?: string) => {
      setInventoryCreateDialog({ open: true, matterId, inventoryType });
    },
    []
  );

  const closeInventoryCreateDialog = useCallback(() => {
    setInventoryCreateDialog(null);
  }, []);

  // Correspondence dialog handlers
  const openCorrespondenceDialog = useCallback(
    (
      matterId: number,
      institutionId: number,
      type: 'notification' | 'follow-up' | 'collection-request',
      parentCorrespondenceId?: number
    ) => {
      setCorrespondenceDialog({
        open: true,
        matterId,
        institutionId,
        type,
        parentCorrespondenceId
      });
    },
    []
  );

  const closeCorrespondenceDialog = useCallback(() => {
    setCorrespondenceDialog(null);
  }, []);

  // Estate details dialog handlers
  const openEstateDetailsDialog = useCallback((matterId: number) => {
    setEstateDetailsDialog({ open: true, matterId });
  }, []);

  const closeEstateDetailsDialog = useCallback(() => {
    setEstateDetailsDialog(null);
  }, []);

  // Manage matter dialog handlers (non-estate)
  const openManageMatterDialog = useCallback((matterId: number) => {
    setManageMatterDialog({ open: true, matterId });
  }, []);

  const closeManageMatterDialog = useCallback(() => {
    setManageMatterDialog(null);
  }, []);

  // Notifications workflow dialog handlers
  const openNotificationsWorkflow = useCallback((matterId: number) => {
    setNotificationsWorkflowDialog({ open: true, matterId });
  }, []);

  const closeNotificationsWorkflow = useCallback(() => {
    setNotificationsWorkflowDialog(null);
  }, []);

  // Collection workflow dialog handlers
  const openCollectionWorkflow = useCallback((matterId: number) => {
    setCollectionWorkflowDialog({ open: true, matterId });
  }, []);

  const closeCollectionWorkflow = useCallback(() => {
    setCollectionWorkflowDialog(null);
  }, []);

  // View Forms dialog handlers
  const openViewFormsDialog = useCallback(
    (matterId: number, institutionId: number, institutionName: string) => {
      setViewFormsDialog({
        open: true,
        matterId,
        institutionId,
        institutionName
      });
    },
    []
  );

  const closeViewFormsDialog = useCallback(() => {
    setViewFormsDialog(null);
  }, []);

  // Discovery sheet handlers (registered by DiscoverySheetProvider when mounted)
  const letterEditorHandlerRef = useRef<
    ((institutionId: number) => void) | null
  >(null);
  const correspondenceHandlerRef = useRef<
    ((institutionId: number) => void) | null
  >(null);
  const followUpHandlerRef = useRef<((institutionId: number) => void) | null>(
    null
  );

  const openLetterEditorSheet = useCallback((institutionId: number) => {
    letterEditorHandlerRef.current?.(institutionId);
  }, []);

  const openCorrespondenceSheet = useCallback((institutionId: number) => {
    correspondenceHandlerRef.current?.(institutionId);
  }, []);

  const openFollowUpSheet = useCallback((institutionId: number) => {
    followUpHandlerRef.current?.(institutionId);
  }, []);

  const registerDiscoverySheets = useCallback(
    (
      handlers: {
        openLetterEditorSheet: (institutionId: number) => void;
        openCorrespondenceSheet: (institutionId: number) => void;
        openFollowUpSheet: (institutionId: number) => void;
      } | null
    ) => {
      letterEditorHandlerRef.current = handlers?.openLetterEditorSheet ?? null;
      correspondenceHandlerRef.current =
        handlers?.openCorrespondenceSheet ?? null;
      followUpHandlerRef.current = handlers?.openFollowUpSheet ?? null;
    },
    []
  );

  // Requirements sheet handler (registered by RedemptionSheetProvider when mounted)
  const requirementsSheetHandlerRef = useRef<
    ((institutionId: number) => void) | null
  >(null);

  const openRequirementsSheet = useCallback((institutionId: number) => {
    requirementsSheetHandlerRef.current?.(institutionId);
  }, []);

  const registerRequirementsSheet = useCallback(
    (handler: ((institutionId: number) => void) | null) => {
      requirementsSheetHandlerRef.current = handler;
    },
    []
  );

  // Redemption letter editor sheet handler (registered by RedemptionSheetProvider when mounted)
  const redemptionLetterEditorHandlerRef = useRef<
    ((institutionId: number) => void) | null
  >(null);

  const openRedemptionLetterEditorSheet = useCallback(
    (institutionId: number) => {
      redemptionLetterEditorHandlerRef.current?.(institutionId);
    },
    []
  );

  const registerRedemptionLetterEditorSheet = useCallback(
    (handler: ((institutionId: number) => void) | null) => {
      redemptionLetterEditorHandlerRef.current = handler;
    },
    []
  );

  const value: CTADialogContextValue = {
    fileUploadDialog,
    openFileUploadDialog,
    closeFileUploadDialog,
    contactCreateDialog,
    openContactCreateDialog,
    closeContactCreateDialog,
    inventoryCreateDialog,
    openInventoryCreateDialog,
    closeInventoryCreateDialog,
    correspondenceDialog,
    openCorrespondenceDialog,
    closeCorrespondenceDialog,
    estateDetailsDialog,
    openEstateDetailsDialog,
    closeEstateDetailsDialog,
    manageMatterDialog,
    openManageMatterDialog,
    closeManageMatterDialog,
    notificationsWorkflowDialog,
    openNotificationsWorkflow,
    closeNotificationsWorkflow,
    collectionWorkflowDialog,
    openCollectionWorkflow,
    closeCollectionWorkflow,
    viewFormsDialog,
    openViewFormsDialog,
    closeViewFormsDialog,
    openLetterEditorSheet,
    openCorrespondenceSheet,
    openFollowUpSheet,
    registerDiscoverySheets,
    openRequirementsSheet,
    registerRequirementsSheet,
    openRedemptionLetterEditorSheet,
    registerRedemptionLetterEditorSheet
  };

  return (
    <CTADialogContext value={value}>
      {children}
      <CTADialogRenderer />
    </CTADialogContext>
  );
}

// Hook to use the context
export function useCTADialogs() {
  const context = use(CTADialogContext);
  if (!context) {
    throw new Error('useCTADialogs must be used within CTADialogProvider');
  }
  return context;
}

// Export dialog state types and context value type
export type {
  FileUploadDialogState,
  ContactCreateDialogState,
  InventoryCreateDialogState,
  CorrespondenceDialogState,
  EstateDetailsDialogState,
  ManageMatterDialogState,
  CTADialogContextValue
};
